'use client';

import React, { useEffect, useRef } from 'react';
import { trackEvent } from '@/components/GoogleAnalytics';

interface CalendlyModalProps {
  isOpen: boolean;
  onClose: () => void;
  calendlyUrl: string;
}

export default function CalendlyModal({ isOpen, onClose, calendlyUrl }: CalendlyModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    // Track Calendly modal open
    trackEvent('calendly_open', { event_category: 'engagement', calendly_url: calendlyUrl });
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, calendlyUrl]);

  useEffect(() => {
    if (!isOpen) return;

    // Load Calendly widget script
    const existingScript = document.getElementById('calendly-script');
    if (!existingScript) {
      const script = document.createElement('script');
      script.id = 'calendly-script';
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      document.head.appendChild(script);
    }

    // Load Calendly CSS
    const existingLink = document.getElementById('calendly-css');
    if (!existingLink) {
      const link = document.createElement('link');
      link.id = 'calendly-css';
      link.rel = 'stylesheet';
      link.href = 'https://assets.calendly.com/assets/external/widget.css';
      document.head.appendChild(link);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === overlayRef.current) onClose();
  };

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4"
      style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)' }}
    >
      <div
        className="relative w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl flex flex-col"
        style={{
          background: '#141c28',
          border: '1px solid rgba(99,179,237,0.2)',
          boxShadow: '0 25px 60px rgba(0,0,0,0.5)',
          height: '92vh',
          maxHeight: '92vh',
        }}
      >
        {/* Header */}
        <div
          className="flex-shrink-0 flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4"
          style={{ borderBottom: '1px solid rgba(200,206,214,0.08)' }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{
                background: 'rgba(99,179,237,0.1)',
                border: '1px solid rgba(99,179,237,0.25)',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect
                  x="1.5"
                  y="2"
                  width="13"
                  height="12"
                  rx="2"
                  stroke="#63b3ed"
                  strokeWidth="1.3"
                />
                <path d="M5 1v2M11 1v2" stroke="#63b3ed" strokeWidth="1.3" strokeLinecap="round" />
                <path d="M1.5 6h13" stroke="#63b3ed" strokeWidth="1.3" />
                <rect x="4" y="8.5" width="2" height="2" rx="0.5" fill="#63b3ed" />
                <rect x="7" y="8.5" width="2" height="2" rx="0.5" fill="#63b3ed" />
                <rect x="10" y="8.5" width="2" height="2" rx="0.5" fill="#63b3ed" />
              </svg>
            </div>
            <div>
              <h2
                className="font-display font-bold text-sm sm:text-base"
                style={{ color: '#EDF2F7' }}
              >
                Randevu Al
              </h2>
              <p className="text-xs" style={{ color: 'rgba(237,242,247,0.45)' }}>
                Uygun bir zaman seçin
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center transition-all active:scale-95 hover:scale-110"
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(200,206,214,0.1)',
              color: 'rgba(237,242,247,0.5)',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M2 2l10 10M12 2L2 12"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Calendly Inline Widget */}
        <div
          className="calendly-inline-widget flex-1 min-h-0 rounded-b-2xl overflow-hidden"
          data-url={`${calendlyUrl}?hide_gdpr_banner=1&background_color=141c28&text_color=EDF2F7&primary_color=63b3ed`}
          style={{ minWidth: '280px', width: '100%', height: '100%' }}
        />
      </div>
    </div>
  );
}
